import { Injectable } from '@angular/core';
import { ApiService } from '../../../core/services/api.service';
import { catchError, Observable, throwError } from 'rxjs';
import { APP_CONFIG } from '../../../config';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  constructor(private apiService: ApiService<any>) {}

  private apiUrl = `api:Aw50otLs${APP_CONFIG.stagingName}`;
  private userNotificationUrl = `api:oVl64uWz${APP_CONFIG.stagingName}`;

  addNotification(notification: FormData): Observable<any> {
    return this.apiService
      .add(`${this.apiUrl}/notifications`, notification)
      .pipe(
        catchError((error) => {
          return throwError(
            () => new Error(error.error?.message || error.message || 'Failed to create notification. Please try again.')
          );
        })
      );
  }

  addCustomNotification(notification: FormData): Observable<any> {
    return this.apiService
      .add(`${this.apiUrl}/send_contest_notification`, notification)
      .pipe(
        catchError((error) => {
          return throwError(
            () => new Error(error.error?.message || error.message || 'Failed to create notification. Please try again.')
          );
        })
      );
  }

  getNotificationHistory(
    skipLoaderOrParams: boolean | any = false,
    params?: any
  ): Observable<any> {
    const skipLoader =
      typeof skipLoaderOrParams === 'boolean' ? skipLoaderOrParams : false;
    const queryParams =
      typeof skipLoaderOrParams === 'boolean' ? params : skipLoaderOrParams;

    return this.apiService
      .getAllByParams<any>(`${this.apiUrl}/notif_admin`, {
        params: queryParams,
        skipLoader,
      })
      .pipe(
        catchError((error) => {
          return throwError(
            () =>
              new Error(
                error.error?.message || 'Failed to load notification history.'
              )
          );
        })
      );
  }

  getUserNotifications(params?: any): Observable<any> {
    return this.apiService
      .getAllByParams<any>(`${this.apiUrl}/user_Notification`, {
        params,
      })
      .pipe(
        catchError((error) => {
          return throwError(
            () =>
              new Error(
                error.error?.message || 'Failed to load admin notifications.'
              )
          );
        })
      );
  }

  getNotificationsByType(notificationType: string, params?: any, skipLoader = false): Observable<any> {
    return this.apiService
      .getAllByParams<any>(`${this.userNotificationUrl}/get_notifications`, {
        params: {
          notification_type: notificationType,
          ...(params || {}),
        },
        skipLoader,
      })
      .pipe(
        catchError((error) => {
          return throwError(
            () =>
              new Error(
                error.error?.message || 'Failed to load notifications.'
              )
          );
        })
      );
  }

  getNotificationLogs(params?: any, skipLoader = false): Observable<any> {
    return this.apiService
      .getAllByParams<any>(`${this.apiUrl}/notifications`, {
        params,
        skipLoader,
      })
      .pipe(
        catchError((error) => {
          return throwError(
            () =>
              new Error(
                error.error?.message || 'Failed to load notification request.'
              )
          );
        })
      );
  }

  getNotificationUnread(skipLoader = false): Observable<any> {
    return this.apiService
      .getAll<any>(`${this.apiUrl}/get_notification_unread`, skipLoader)
      .pipe(
        catchError((error) => {
          return throwError(
            () =>
              new Error(
                error.error?.message ||
                  'Failed to load unread notification count.'
              )
          );
        })
      );
  }

  editNotification(id: number, notificationData: any) {
    return this.apiService
      .update<any>(`${this.apiUrl}/notifications`, id, notificationData)
      .pipe(
        catchError((error) => {
          return throwError(
            () =>
              new Error(
                error.error?.message || 'Failed to update subscription.'
              )
          );
        })
      );
  }

  deleteNotification(notificationId: number): Observable<void> {
    return this.apiService
      .deleteRequestById<void>(`${this.apiUrl}/notifications`, notificationId)
      .pipe(
        catchError((error) => {
          return throwError(
            () =>
              new Error(
                error.error?.message || 'Failed to delete notification.'
              )
          );
        })
      );
  }

  markAsRead(payload: any): Observable<any> {
    return this.apiService
      .add(`${this.apiUrl}/notification_update_client`, payload)
      .pipe(
        catchError((error) => {
          return throwError(
            () =>
              new Error(
                error.error?.message || 'Failed to mark notification as read.'
              )
          );
        })
      );
  }

  markAllAsRead(payload: { notification_ids: number[] }): Observable<any> {
    return this.apiService
      .add(`${this.apiUrl}/mark_allnotifications_read`, payload)
      .pipe(
        catchError((error) => {
          return throwError(
            () =>
              new Error(
                error.error?.message || 'Failed to mark all notifications as read.'
              )
          );
        })
      );
  }

  markAllAsReadAdmin(payload: { notification_ids: number[] }): Observable<any> {
    return this.apiService
      .add(`${this.apiUrl}/mark_allnotifications_read_admin`, payload)
      .pipe(
        catchError((error) => {
          return throwError(
            () =>
              new Error(
                error.error?.message ||
                  'Failed to mark all notifications as read.'
              )
          );
        })
      );
  }

  getContestLookup(): Observable<any> {
    return this.apiService.getAll<any>(`${this.apiUrl}/contests_lookup`).pipe(
      catchError((error) => {
        return throwError(
          () => new Error(error.error?.message || error.message || 'Failed to get contest lookup.')
        );
      })
    );
  }
}
